import React, { ChangeEvent, ReactNode, ReactElement } from "react";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import Table from "@material-ui/core/Table";
import Paper from "@material-ui/core/Paper";
import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import TablePagination from "./TablePagination";
import TableToolbar from "./TableToolbar";
import {
  DetailCardField,
  DetailCardTitleProperty,
  SortTableBy,
  TableColumn,
  TableData,
  TableOrder,
  DetailCardAction
} from "./types";
import { Theme, CircularProgress, Typography } from "@material-ui/core";

type State = {
  data: TableData[];
  order: TableOrder;
  orderBy: SortTableBy;
  page: number;
  selected: string[];
  rowsPerPage: number;
};

type Props = {
  classes?: any;
  columns: TableColumn[];
  onRowClickGetDetailData?: (rowId: string) => Promise<void>;
  detailCardTitle?: DetailCardTitleProperty;
  detailCardActions?: DetailCardAction[];
  data: TableData[];
  error?: Error;
  filterFields?: ReactElement;
  loading: boolean;
  onFilterReset?: () => void;
  onFilterSubmit?: () => void;
  onSearchUpdate?: (e: any) => void;
  onSearchSubmit?: () => void;
  onSearchReset?: () => void;
  onSelectedAction?: (selectedElementIds: string[]) => void;
  orderByDefault?: TableOrder;
  renderDetailCard?: (data: DetailCardField[]) => ReactNode;
  rowsPerPage?: number;
  searchValue?: string;
  selectedActionIcon?: string;
  sortByDefault: SortTableBy;
  title: string;
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: "fit-content",
      width: "100%"
    },
    loadingWrapper: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(4)
    },
    tableWrapper: {
      overflowX: "auto"
    },
    title: {
      position: "absolute",
      top: "-9999px",
      left: "-9999px"
    }
  });

class DataTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      data: props.data,
      order: props.orderByDefault || TableOrder.Desc,
      orderBy: props.sortByDefault,
      selected: [],
      page: 0,
      rowsPerPage: props.rowsPerPage || 5
    };
    this.onSubmitFilter = this.onSubmitFilter.bind(this);
    this.onSubmitSearch = this.onSubmitSearch.bind(this);
  }

  onRequestSort = (property: SortTableBy) => {
    const orderBy = property;
    let order = TableOrder.Desc;

    if (this.state.orderBy.id === property.id && this.state.order === "desc") {
      order = TableOrder.Asc;
    }

    this.setState({ order, orderBy });
  };

  onSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selected = this.props.data.map(n => n.id);
      this.setState(() => ({ selected }));
      return;
    }
    this.setState({ selected: [] });
  };

  onSelectRow = (id: string) => {
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    this.setState({ selected: newSelected });
  };

  onChangePage = (event: any, page: number) => {
    this.setState({ page });
  };

  onChangeRowsPerPage = (event: any) => {
    this.setState({ page: 0, rowsPerPage: event.target.value });
  };

  onSelectedAction = () => {
    this.props.onSelectedAction &&
      this.props.onSelectedAction(this.state.selected);
  };

  onSubmitFilter() {
    this.setState({
      page: 0
    });
    this.props.onFilterSubmit && this.props.onFilterSubmit();
  }

  onSubmitSearch() {
    this.setState({
      page: 0
    });
    this.props.onSearchSubmit && this.props.onSearchSubmit();
  }

  checkSelected = (id: string) => this.state.selected.indexOf(id) !== -1;

  render() {
    const {
      classes,
      columns,
      data,
      onRowClickGetDetailData,
      detailCardActions,
      detailCardTitle,
      error,
      filterFields,
      loading,
      onFilterReset,
      onSearchUpdate,
      onSearchReset,
      onSelectedAction,
      renderDetailCard,
      searchValue,
      selectedActionIcon,
      title
    } = this.props;
    const { order, orderBy, page, rowsPerPage, selected } = this.state;

    const noData = !data || !data.length;
    const errorMessage = error
      ? // Prioritise error msgs over no data msgs
        error.message
      : noData
      ? "Oops! Nothing to see here..."
      : null;

    return (
      <div className={classes.root} id={title}>
        <Typography id="tableTitle" className={classes.title}>
          {title}
        </Typography>
        <TableToolbar
          columns={columns}
          filterFields={filterFields}
          numSelected={selected.length}
          onFilterReset={onFilterReset}
          onFilterSubmit={this.onSubmitFilter}
          onSearchSubmit={this.onSubmitSearch}
          onSearchUpdate={onSearchUpdate}
          onSearchReset={onSearchReset}
          searchValue={searchValue}
          selectedAction={this.onSelectedAction}
          selectedActionIcon={selectedActionIcon}
        />

        <Paper className={classes.tableWrapper}>
          {loading ? (
            // If we're loading, display it
            <div className={classes.loadingWrapper}>
              <CircularProgress />
            </div>
          ) : // If there's an error message, display it
          errorMessage ? (
            <div className={classes.loadingWrapper}>
              <Typography color="error">{errorMessage}</Typography>
            </div>
          ) : (
            // If we're ready, display it
            <Table className={classes.table} aria-labelledby="tableTitle">
              <TableHeader
                columns={columns.filter(c => !c.hide)}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                selectable={!!onSelectedAction}
                onSelectAllClick={this.onSelectAllClick}
                onRequestSort={this.onRequestSort}
                rowCount={data.length}
              />
              <TableBody
                data={data}
                columns={columns}
                onRowClickGetDetailData={onRowClickGetDetailData}
                detailCardActions={detailCardActions}
                detailCardTitle={detailCardTitle}
                order={order}
                orderBy={orderBy}
                selectable={!!onSelectedAction}
                checkSelected={this.checkSelected}
                rowsPerPage={rowsPerPage}
                page={page}
                onSelectRow={this.onSelectRow}
                renderDetailCard={renderDetailCard}
              />
            </Table>
          )}
        </Paper>

        <TablePagination
          totalRowCount={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.onChangePage}
          onRowCountChange={this.onChangeRowsPerPage}
        />
      </div>
    );
  }
}

const styled = withStyles(styles)(DataTable);

export { styled as DataTable };
